import React, { useState, useCallback, memo, useMemo, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/store';
import classNames from 'classnames';
import { useRole } from '@/hooks/useRole';

// Icons
import {
  FiHome,
  FiSettings,
  FiUsers,
  FiDatabase,
  FiBarChart2,
  FiLayers,
  FiChevronDown,
  FiChevronRight,
  FiPieChart,
  FiWifi,
  FiChevronsLeft,
  FiChevronsRight,
  FiClock,
  FiSliders
} from 'react-icons/fi';

interface SidebarProps {
  expanded: boolean;
}

interface SidebarItemProps {
  title: React.ReactNode;
  icon: React.ReactNode;
  path: string;
  children?: { title: string; path: string }[];
  exactPath?: boolean;
}

// Memoize the SidebarItem component to prevent unnecessary re-renders
const SidebarItem: React.FC<SidebarItemProps & { isExpanded: boolean }> = memo(({ title, icon, path, children, exactPath, isExpanded }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { mode } = useSelector((state: RootState) => state.theme);
  const navigateTo = useNavigate();

  // Fix for multiple active items - use exact path matching when specified
  const isActive = exactPath 
    ? location.pathname === path 
    : location.pathname.startsWith(path);
    
  const hasChildren = children && children.length > 0;

  // 只在组件挂载时检查一次是否需要展开子菜单
  useEffect(() => {
    if (hasChildren) {
      const hasActiveChild = children.some(child => 
        location.pathname === child.path || location.pathname.startsWith(child.path)
      );
      if (hasActiveChild) {
        setOpen(true);
      }
    }
  }, []);

  // Use useCallback to memoize the function
  const toggleDropdown = useCallback((e: React.MouseEvent) => {
    if (hasChildren && children.length > 0) {
      e.preventDefault();
      
      // 获取第一个子节点的路径
      const firstChildPath = children[0].path;
      
      if (isExpanded) {
        // 展开状态：展开子菜单，并导航到第一个子节点
        setOpen(prevOpen => {
          // 如果已经是展开状态，只切换展开状态而不导航
          if (prevOpen) {
            return false;
          } else {
            // 如果是折叠状态，展开并导航到第一个子节点
            navigateTo(firstChildPath);
            return true;
          }
        });
      } else {
        // 收缩状态：直接导航到第一个子节点，不展开子菜单
        navigateTo(firstChildPath);
      }
    }
  }, [hasChildren, children, isExpanded, navigateTo]);

  return (
    <div className="mb-1">
      {hasChildren ? (
        // If it has children, use a div with onClick for dropdown toggle
        <div
          className={classNames(
            'flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors',
            {
              'bg-primary-500 text-white': isActive,
              'hover:bg-gray-100': !isActive && mode === 'light',
              'hover:bg-gray-700': !isActive && mode === 'dark',
              'text-gray-900': !isActive && mode === 'light',
              'text-gray-200': !isActive && mode === 'dark',
              'justify-center': !isExpanded
            }
          )}
          onClick={toggleDropdown}
        >
          <div className={classNames("text-lg", { "mr-3": isExpanded })}>{icon}</div>
          {isExpanded && (
            <>
              <span className="text-sm font-medium flex-grow">{title}</span>
              <div className="text-lg">
                {open ? <FiChevronDown /> : <FiChevronRight />}
              </div>
            </>
          )}
        </div>
      ) : (
        // If no children, use NavLink for client-side navigation
        <NavLink
          to={path}
          end={exactPath}
          className={({ isActive }) =>
            classNames(
              'flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors',
              {
                'bg-primary-500 text-white': isActive,
                'hover:bg-gray-100': !isActive && mode === 'light',
                'hover:bg-gray-700': !isActive && mode === 'dark',
                'text-gray-900': !isActive && mode === 'light',
                'text-gray-200': !isActive && mode === 'dark',
                'justify-center': !isExpanded
              }
            )
          }
        >
          <div className={classNames("text-lg", { "mr-3": isExpanded })}>{icon}</div>
          {isExpanded && <span className="text-sm font-medium flex-grow">{title}</span>}
        </NavLink>
      )}

      {/* Dropdown menu - only show when expanded */}
      {hasChildren && open && isExpanded && (
        <div className="pl-10 mt-1">
          {children.map((item, index) => (
            <NavLink
              key={index}
              to={item.path}
              end
              className={({ isActive }) =>
                classNames('block py-2 px-3 text-sm rounded-md', {
                  'bg-primary-500 text-white': isActive,
                  'text-gray-700 hover:bg-gray-100': !isActive && mode === 'light',
                  'text-gray-300 hover:bg-gray-700': !isActive && mode === 'dark',
                })
              }
            >
              {item.title}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
});

// Memoize the Sidebar component to prevent unnecessary re-renders
const Sidebar: React.FC<SidebarProps> = memo(({ expanded }) => {
  const { mode } = useSelector((state: RootState) => state.theme);
  const { userRole, isAdmin, isUser, isInstaller } = useRole();
  const location = useLocation();
  
  // Add this to get dispatch
  const dispatch = useDispatch();
  
  // Add a toggle handler for sidebar expansion
  const handleToggleSidebar = useCallback(() => {
    // We'll need to implement this in the parent component or use Redux
    // For now, let's assume we'll use a custom event
    const event = new CustomEvent('toggle-sidebar');
    window.dispatchEvent(event);
  }, []);

  // Memoize menu items to prevent recreating them on each render
  const commonItems = useMemo<SidebarItemProps[]>(() => [
    {
      title: 'Dashboard',
      icon: <FiHome />,
      path: '/dashboard',
      exactPath: true,
    },
    {
      title: 'Devices',
      icon: <FiSliders />,
      path: '/devices',
      exactPath: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          Analytics
          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 rounded">
            WIP
          </span>
        </div>
      ),
      icon: <FiPieChart />,
      path: '/analytics',
      exactPath: false,
      children: [
        // { title: 'Overview', path: '/analytics' },
        { title: 'WiFi Signal', path: '/analytics/wifi-signal' },
        { title: 'Flow Rate', path: '/analytics/flow-rate' },
        { title: 'pH Value', path: '/analytics/ph-value' },
        { title: 'ORP Value', path: '/analytics/orp-value' },
        { title: 'Temperature', path: '/analytics/water-temperature' },
        { title: 'Aux Status', path: '/analytics/aux-status' },
      ],
    },
    {
      title: 'Schedule',
      icon: <FiClock />,
      path: '/schedule',
      exactPath: true,
      children: [
        { title: 'Overview', path: '/schedule-overview' },
        { title: 'Timeline', path: '/schedule-timeline' },
      ],
    },
    {
      title: 'Settings',
      icon: <FiSettings />,
      path: '/settings',
      exactPath: false,
      children: [
        { title: 'Device Setup', path: '/settings/device-setup' },
        { title: 'Users & Sharing', path: '/settings/users-sharing' },
        // { title: 'History', path: '/settings/history' },
        // { title: 'Sensors/Calibration', path: '/settings/sensors-calibration' },
        { title: 'Device Settings', path: '/settings/device-settings' },
      ],
    }
  ], []);

  // Admin-specific menu items
  const adminItems = useMemo<SidebarItemProps[]>(() => [
    {
      title: 'Sites',
      icon: <FiLayers />,
      path: '/sites',
      exactPath: false,
      children: [
        { title: 'All Sites', path: '/sites' },
      ],
    },
    {
      title: 'Collection Tasks',
      icon: <FiDatabase />,
      path: '/collection-tasks',
      exactPath: true,
    },
    {
      title: 'Operation',
      icon: <FiDatabase />,
      path: '/operation',
      exactPath: true,
    },
  ], []);

  // User-specific menu items
  const userItems = useMemo<SidebarItemProps[]>(() => [
    {
      title: 'Sites',
      icon: <FiLayers />,
      path: '/sites',
      exactPath: true,
    },

  ], []);

  // Installer-specific menu items
  const installerItems = useMemo<SidebarItemProps[]>(() => [
    {
      title: 'Installations',
      icon: <FiLayers />,
      path: '/installations',
      exactPath: true,
    },
    {
      title: 'Reports',
      icon: <FiBarChart2 />,
      path: '/reports',
      exactPath: true,
    },
  ], []);

  // Determine which items to show based on role
  const sidebarItems = useMemo(() => {
    let items = [...commonItems];

    if (isAdmin()) {
      items = [...items, ...adminItems];
    } else if (isUser()) {
      items = [...items, ...userItems];
    } else if (isInstaller()) {
      items = [...items, ...installerItems];
    }

    return items;
  }, [commonItems, adminItems, userItems, installerItems, isAdmin, isUser, isInstaller, location.pathname]);

  // Get role display name
  const getRoleDisplayName = useCallback(() => {
    if (isAdmin()) return 'Administrator';
    if (isUser()) return 'User';
    if (isInstaller()) return 'Installer';
    return 'Guest';
  }, [isAdmin, isUser, isInstaller]);

  // Get role badge color
  const getRoleBadgeColor = useCallback(() => {
    if (isAdmin()) return 'bg-purple-600';
    if (isUser()) return 'bg-blue-600';
    if (isInstaller()) return 'bg-green-600';
    return 'bg-gray-600';
  }, [isAdmin, isUser, isInstaller]);

  const auth = useSelector((state: RootState) => state.auth);

  // 获取用户头像首字母
  const getUserInitial = useCallback(() => {
    if (auth.user?.firstName) {
      return auth.user.firstName.charAt(0).toUpperCase();
    }
    if (auth.user?.username) {
      return auth.user.username.charAt(0).toUpperCase();
    }
    return 'U';
  }, [auth.user]);

  return (
    <div
      className={classNames(
        'flex flex-col h-screen overflow-y-auto transition-all duration-300 ease-in-out relative',
        {
          'w-64': expanded,
          'w-20': !expanded,
          'bg-white border-r border-gray-200': mode === 'light',
          'bg-gray-800 border-r border-gray-700': mode === 'dark',
        }
      )}
    >
      {/* Toggle Button - Positioned on the right edge */}
      <button 
        onClick={handleToggleSidebar}
        className={classNames(
          "absolute top-20 -right-3 w-6 h-6 rounded-full flex items-center justify-center shadow-md z-10 transition-colors",
          {
            "bg-white text-gray-600 hover:bg-gray-100": mode === 'light',
            "bg-gray-700 text-gray-300 hover:bg-gray-600": mode === 'dark'
          }
        )}
        aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
      >
        {expanded ? <FiChevronsLeft size={14} /> : <FiChevronsRight size={14} />}
      </button>

      {/* Sidebar header */}
      <div className="flex items-center justify-center h-16 border-b">
        <div className="flex items-center">
          {expanded ? (
            <img 
              src={mode === 'light' 
                ? "/splash-me-horizontal-v1-full-color-rgb-500px@72ppi.png"
                : "/splash-me-horizontal-v1-inverted-rgb-500px@72ppi.png"
              }
              alt="SplashMe"
              className="h-8 w-32 object-contain"
            />
          ) : (
            <div className="w-8 h-8 flex items-center justify-center">
              {/* Inline SVG from android ic_profile.xml */}
              <svg
                className="w-6 h-6"
                viewBox="0 0 44.874 44.874"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M15.138,0A15.155,15.155 0,0 0,0 15.138L0,44.874L29.736,44.874A15.155,15.155 0,0 0,44.874 29.736L44.874,0ZM29.738,40.548L4.325,40.548L4.325,26.943a8.589,8.589 0,0 0,4.326 1.17h0.541a8.634,8.634 0,0 0,6.488 -2.934,8.631 8.631,0 0,0 6.487,2.934h0.541a8.633,8.633 0,0 0,6.487 -2.934,8.634 8.634,0 0,0 6.488,2.934h0.541a8.586,8.586 0,0 0,4.325 -1.17v2.792A10.825,10.825 0,0 1,29.736 40.548M40.548,19.463a4.33,4.33 0,0 1,-4.325 4.325h-0.541a4.329,4.329 0,0 1,-4.325 -4.325v-0.541L27.032,18.922v0.541a4.329,4.329 0,0 1,-4.324 4.325h-0.541a4.33,4.33 0,0 1,-4.325 -4.325v-0.27L13.516,19.193v0.27a4.33,4.33 0,0 1,-4.325 4.325L8.651,23.788a4.33,4.33 0,0 1,-4.326 -4.325L4.325,15.138A10.826,10.826 0,0 1,15.138 4.325h25.41L40.548,19.463Z"
                  fill="#5de878"
                />
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar content */}
      <div className="flex-grow p-4">
        <div className="space-y-1">
          {sidebarItems.map((item, index) => (
            <SidebarItem
              key={index}
              title={item.title}
              icon={item.icon}
              path={item.path}
              children={item.children}
              exactPath={item.exactPath}
              isExpanded={expanded}
            />
          ))}
        </div>
      </div>

      {/* Sidebar footer - simplified when collapsed */}
      <div className={classNames("p-4 border-t", {
        'border-gray-200': mode === 'light',
        'border-gray-700': mode === 'dark'
      })}>
        {expanded ? (
          <div className="flex flex-col space-y-2">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white shadow-sm">
                <span className="text-base font-medium">{getUserInitial()}</span>
              </div>
              <div className="ml-3 flex-grow">
                <p className="text-sm font-medium truncate">{auth.user?.firstName || auth.user?.username || 'User'}</p>
                <p className="text-xs text-gray-500 truncate">{auth.user?.email || 'user@example.com'}</p>
              </div>
            </div>
            <div className={`px-3 py-1.5 rounded-md text-xs font-medium text-white ${getRoleBadgeColor()} flex items-center justify-center`}>
              {getRoleDisplayName()}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white shadow-sm">
              <span className="text-base font-medium">{getUserInitial()}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default Sidebar;
